import React, { useState, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import { Query } from "react-apollo";
import { loader } from "graphql.macro";

// Import your components
import Header from "../Header";
import Nav from "../Nav";
import Footer from "../Footer";
import Loading from "../../Loading";
import Tooltip from "../DrawRequests/LoanNumber";
import FileUpload from "./FileUpload";
// import FileUploadTest from "./FileUploadTest";

const GET_BRANCH_QUERY = loader("../../../graphql/GET_BRANCH_QUERY.graphql");

const DrawRequest = (props) => {
  // Fixed branch ID - using the same one from your App.js
  const id = "a2APo0000000Io9MAE";

  const [formData, setFormData] = useState({
    loanNumber: "",
    clientFirstName: "",
    clientLastName: "",
    propertyAddress: "",
    propertyCity: "",
    propertyState: "",
    builderCompanyName: "",
    builderOfficeName: "",
    builderContactName: "",
    builderContactPhone: "",
    builderContactEmail: "",
    drawType: "",
    additionalDocuments: [], // New field for file uploads
  });

  const [errors, setErrors] = useState({
    loanNumber: "",
    clientFirstName: "",
    clientLastName: "",
    propertyAddress: "",
    propertyCity: "",
    propertyState: "",
    builderCompanyName: "",
    builderOfficeName: "",
    builderContactName: "",
    builderContactPhone: "",
    builderContactEmail: "",
    drawType: "",
    additionalDocuments: "", // New field for document upload errors
  });

  const [tooltips, setTooltips] = useState({
    loanNumber: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // For phone number formatting
  const handlePhoneChange = (e) => {
    let input = e.target.value;
    input = input.replace(/[^0-9]/g, "");
    if (input.length > 3) {
      input = `(${input.substring(0, 3)}) ${input.substring(3)}`;
    }
    if (input.length > 9) {
      input = `${input.substring(0, 9)}-${input.substring(9)}`;
    }
    input = input.substring(0, 14);

    setFormData({
      ...formData,
      [e.target.name]: input,
    });
  };

  // New handler for clearing errors on focus
  const handleFocus = (e) => {
    const { name } = e.target;
    // Clear the error for the focused field
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  // Toggle tooltip visibility
  const toggleTooltip = (name) => {
    setTooltips({
      ...tooltips,
      [name]: !tooltips[name],
    });
  };

  // Email validation
  const emailValidation = (email) => {
    let emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
  };

  // Handle file uploads
  const handleFilesChange = (files) => {
    setFormData({
      ...formData,
      additionalDocuments: files,
    });
    // Clear any previous file upload errors
    if (errors.additionalDocuments) {
      setErrors({
        ...errors,
        additionalDocuments: "",
      });
    }
  };

  // Handle file upload status changes
  const handleUploadingChange = (uploading) => {
    setIsUploadingFiles(uploading);
  };

  // Replace your handleSubmit function with this simplified version:

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prevent submission if files are still uploading
    if (isUploadingFiles) {
      return;
    }

    setIsLoading(true);

    // Form validation
    let validationErrors = {
      loanNumber: "",
      clientFirstName: "",
      clientLastName: "",
      propertyAddress: "",
      propertyCity: "",
      propertyState: "",
      builderCompanyName: "",
      builderOfficeName: "",
      builderContactName: "",
      builderContactPhone: "",
      builderContactEmail: "",
      drawType: "",
    };

    // Check for required fields
    if (!formData.loanNumber) {
      validationErrors.loanNumber = "Loan Number is required";
    }
    if (!formData.clientFirstName) {
      validationErrors.clientFirstName = "Client First Name is required";
    }
    if (!formData.clientLastName) {
      validationErrors.clientLastName = "Client Last Name is required";
    }
    if (!formData.propertyAddress) {
      validationErrors.propertyAddress = "Property Address is required";
    }
    if (!formData.propertyCity) {
      validationErrors.propertyCity = "Property City is required";
    }
    if (!formData.propertyState) {
      validationErrors.propertyState = "Property State is required";
    }
    if (!formData.builderCompanyName) {
      validationErrors.builderCompanyName = "Builder Company Name is required";
    }
    if (!formData.builderOfficeName) {
      validationErrors.builderOfficeName = "Builder Office Name is required";
    }
    if (!formData.builderContactName) {
      validationErrors.builderContactName = "Builder Contact Name is required";
    }
    if (!formData.builderContactPhone) {
      validationErrors.builderContactPhone = "Builder Contact Phone is required";
    }
    if (!formData.builderContactEmail) {
      validationErrors.builderContactEmail = "Builder Contact Email is required";
    } else if (!emailValidation(formData.builderContactEmail)) {
      validationErrors.builderContactEmail = "Invalid Email";
    }
    if (!formData.drawType) {
      validationErrors.drawType = "Draw Type is required";
    }

    // Note: additionalDocuments are optional, so no validation required
    // However, if documents are added, ensure they're properly uploaded
    if (formData.additionalDocuments.length > 0) {
      const hasUnuploadedFiles = formData.additionalDocuments.some((file) => file.status !== "uploaded");
      if (hasUnuploadedFiles) {
        validationErrors.additionalDocuments = "Please wait for all files to finish uploading before submitting";
        setErrors(validationErrors);
        setIsLoading(false);
        return;
      }
    }

    // Check if we have any errors
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    // Prepare data to send with file attachments formatted for Zapier
    const dataToSend = {
      ...formData,
      source: "Draw Request Form",
      fromName: "Huffman Team Website",
      // Create a comma-separated string of Cloudinary URLs
      documentUrls: formData.additionalDocuments.map((file) => file.cloudinaryUrl).join(", "),
    };

    // Remove the additionalDocuments array from the main form data to avoid duplication
    delete dataToSend.additionalDocuments;

    // testURL = "https://hooks.zapier.com/hooks/catch/5841802/uh3ae36/"; // Invalid URL for testing failure
    // realURL = "https://hooks.zapier.com/hooks/catch/5841802/2xkvvfi/";
    // Send data to Zapier webhook with no-cors mode
    fetch("https://hooks.zapier.com/hooks/catch/5841802/2xkvvfi/", {
      method: "POST",
      mode: "no-cors", // This prevents CORS errors
      body: JSON.stringify(dataToSend),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        // With no-cors, we won't get a normal response object
        // We'll assume success since the browser didn't throw an error
        console.log("Request sent successfully");
        setIsLoading(false);
        setShowNotificationModal(true);
        setModalData({
          title: "Success!",
          message: "Your draw request has been submitted successfully.",
        });

        // Reset form
        setFormData({
          loanNumber: "",
          clientFirstName: "",
          clientLastName: "",
          propertyAddress: "",
          propertyCity: "",
          propertyState: "",
          builderCompanyName: "",
          builderOfficeName: "",
          builderContactName: "",
          builderContactPhone: "",
          builderContactEmail: "",
          drawType: "",
          additionalDocuments: [], // Reset file uploads
        });

        // Reset errors
        setErrors({
          loanNumber: "",
          clientFirstName: "",
          clientLastName: "",
          propertyAddress: "",
          propertyCity: "",
          propertyState: "",
          builderCompanyName: "",
          builderOfficeName: "",
          builderContactName: "",
          builderContactPhone: "",
          builderContactEmail: "",
          drawType: "",
          additionalDocuments: "", // Reset file upload errors
        });
      })
      .catch((error) => {
        // This should rarely happen with no-cors mode
        console.error("Error:", error);
        setIsLoading(false);
        setShowNotificationModal(true);
        setModalData({
          title: "Error",
          message: "There was an error submitting your request. Please try again later.",
        });
      });
  };

  return (
    <Query asyncMode query={GET_BRANCH_QUERY} variables={{ id }}>
      {({ data, error, loading }) => {
        if (loading) return <Loading />;

        if (error) {
          console.log(error);
          return `Error: ${error}`;
        }

        const {
          branch: { photoUrl, name },
        } = data;

        return (
          <>
            <Header backgroundImage={photoUrl} name={name} id={id} />
            <Nav />
            <div className="bg-gray-50">
              <div className="min-h-650 pb-8 max-w-[1300px] mx-auto">
                <h2 className="text-4xl lg:text-5xl font-bold text-center text-[#183663] py-16 lg:py-20">
                  Submit a Draw Request
                </h2>

                {/* Main content area with form and sidebar */}
                <div className="flex flex-col lg:flex-row gap-8 px-8 lg:px-0">
                  {/* Form Content Area */}
                  <div className="lg:w-3/4 flex justify-end">
                    <div className="w-full max-w-6xl">
                      <form className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 w-full" onSubmit={handleSubmit}>
                        {/* Loan Number */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <div className="flex items-center mb-1">
                            <label className="text-gray-800 text-[16px] text-left">Loan Number</label>
                            <Tooltip
                              text="Please contact your Loan Officer if you are unsure about the Loan Number"
                              isVisible={tooltips.loanNumber}
                              setIsVisible={(visible) => setTooltips({ ...tooltips, loanNumber: visible })}
                            />
                          </div>
                          <input
                            type="text"
                            name="loanNumber"
                            value={formData.loanNumber}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.loanNumber ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.loanNumber && <p className="text-red-500 mt-1 text-[12px]">{errors.loanNumber}</p>}
                        </div>

                        {/* Client First Name */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Client First Name</label>
                          <input
                            type="text"
                            name="clientFirstName"
                            value={formData.clientFirstName}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.clientFirstName ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.clientFirstName && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.clientFirstName}</p>
                          )}
                        </div>

                        {/* Client Last Name */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Client Last Name</label>
                          <input
                            type="text"
                            name="clientLastName"
                            value={formData.clientLastName}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.clientLastName ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.clientLastName && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.clientLastName}</p>
                          )}
                        </div>

                        {/* Property Address */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">
                            Property Street Address or Lot Number
                          </label>
                          <input
                            type="text"
                            name="propertyAddress"
                            value={formData.propertyAddress}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.propertyAddress ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.propertyAddress && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.propertyAddress}</p>
                          )}
                        </div>

                        {/* Property City */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Property City</label>
                          <input
                            type="text"
                            name="propertyCity"
                            value={formData.propertyCity}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.propertyCity ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.propertyCity && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.propertyCity}</p>
                          )}
                        </div>

                        {/* Property State */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Property State</label>
                          <input
                            type="text"
                            name="propertyState"
                            value={formData.propertyState}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.propertyState ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.propertyState && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.propertyState}</p>
                          )}
                        </div>

                        {/* Builder Company Name */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Builder Company Name</label>
                          <input
                            type="text"
                            name="builderCompanyName"
                            value={formData.builderCompanyName}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.builderCompanyName ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.builderCompanyName && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.builderCompanyName}</p>
                          )}
                        </div>

                        {/* Builder Office Name */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Builder Office Name</label>
                          <input
                            type="text"
                            name="builderOfficeName"
                            value={formData.builderOfficeName}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.builderOfficeName ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.builderOfficeName && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.builderOfficeName}</p>
                          )}
                        </div>

                        {/* Builder Contact Name */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Builder Contact Name</label>
                          <input
                            type="text"
                            name="builderContactName"
                            value={formData.builderContactName}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.builderContactName ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.builderContactName && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.builderContactName}</p>
                          )}
                        </div>

                        {/* Builder Contact Phone */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Builder Contact Phone</label>
                          <input
                            type="text"
                            name="builderContactPhone"
                            value={formData.builderContactPhone}
                            onChange={handlePhoneChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.builderContactPhone ? "border-red-500" : "border-gray-600"
                            } bg-gray-50 rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.builderContactPhone && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.builderContactPhone}</p>
                          )}
                        </div>

                        {/* Builder Contact Email */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">Builder Contact Email</label>
                          <input
                            type="email"
                            name="builderContactEmail"
                            value={formData.builderContactEmail}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-white border ${
                              errors.builderContactEmail ? "border-red-500" : "border-gray-600"
                            } rounded-lg py-3 px-2 w-full text-gray-800 text-[16px]`}
                          />
                          {errors.builderContactEmail && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.builderContactEmail}</p>
                          )}
                        </div>

                        {/* Draw Type */}
                        <div className="flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">
                            Which Draw is Being Requested
                          </label>
                          <select
                            name="drawType"
                            value={formData.drawType}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className={`bg-transparent border ${
                              errors.drawType ? "border-red-500" : "border-gray-600"
                            } rounded-lg py-4 px-2 w-full text-gray-800 text-[16px]`}
                          >
                            <option value="" className="bg-blue-900 text-white">
                              Select a draw type
                            </option>
                            <option value="80_percent_2nd" className="bg-blue-900 text-white">
                              80% 2nd Draw
                            </option>
                            <option value="final_10_percent" className="bg-blue-900 text-white">
                              Final 10% Draw
                            </option>
                            <option value="100_percent_no_draw" className="bg-blue-900 text-white">
                              100% - for No-Draw Loans Only
                            </option>
                          </select>
                          {errors.drawType && <p className="text-red-500 mt-1 text-[12px]">{errors.drawType}</p>}
                        </div>

                        {/* Additional Documents Upload */}
                        <div className="col-span-1 md:col-span-2 flex flex-col w-full justify-center mx-auto py-2">
                          <label className="text-gray-800 text-[16px] text-left mb-1">
                            Additional Documents (Optional)
                          </label>
                          <p className="text-gray-600 text-[14px] mb-3">
                            Upload any supporting documents for your draw request such as invoices, receipts, inspection
                            reports, or photos.
                          </p>
                          <FileUpload
                            files={formData.additionalDocuments}
                            onFilesChange={handleFilesChange}
                            onUploadingChange={handleUploadingChange}
                            maxFiles={10}
                            maxSizePerFile={10 * 1024 * 1024} // 10MB per file
                          />
                          {errors.additionalDocuments && (
                            <p className="text-red-500 mt-1 text-[12px]">{errors.additionalDocuments}</p>
                          )}
                        </div>
                      </form>

                      <div className="flex justify-center mt-10">
                        {!isLoading && !isUploadingFiles ? (
                          <button
                            onClick={handleSubmit}
                            type="submit"
                            className="w-full lg:w-80 my-4 bg-pink-800 hover:bg-pink-900 text-white text-[18px] font-semibold py-2 px-4 rounded-md"
                          >
                            Submit
                          </button>
                        ) : (
                          <div className="flex justify-center">
                            <p className="text-gray-600 text-xl">
                              {isUploadingFiles ? "Uploading files..." : "Submitting..."}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Links Sidebar */}
                  <div className="lg:w-1/4 lg:min-w-[280px] mt-4">
                    <div className="sticky top-8">
                      <div className="bg-[#99c24d] text-[14px] p-4 rounded-t-lg">
                        <h3 className="text-xl font-semibold text-white">Quick Links</h3>
                      </div>
                      <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg p-4 shadow-sm">
                        <div className="space-y-3">
                          <div>
                            <a
                              href="https://calconmutualmortgage.com/assets/draw-request/FHA%20USDA%20Manufactured%20Final%2010_%20or%20100_%20Needs%20Check%20List.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink-800 hover:text-pink-900 hover:underline block text-lg leading-relaxed"
                            >
                              FHA/USDA Manufactured Check List
                            </a>
                          </div>
                          <div>
                            <a
                              href="https://calconmutualmortgage.com/assets/draw-request/Manufactured%20or%20Factory%20Built%2080_%20Needs%20Check%20List.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink-800 hover:text-pink-900 hover:underline block text-lg leading-relaxed"
                            >
                              Pre-Curbside Manufactured Check List
                            </a>
                          </div>
                          <div>
                            <a
                              href="https://calconmutualmortgage.com/assets/draw-request/VA%20Manufactured%20Final%2010_%20or%20100_%20Needs%20Check%20List.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink-800 hover:text-pink-900 hover:underline block text-lg leading-relaxed"
                            >
                              VA Manufactured Check List
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {showNotificationModal && (
                  <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-8 sm:p-6 mx-4 sm:mx-0 w-[80%] lg:w-1/3 relative">
                      <button
                        onClick={() => setShowNotificationModal(false)}
                        className="absolute top-2 right-3 text-gray-600 hover:text-gray-900 text-[18px] font-bold"
                      >
                        ×
                      </button>
                      <h2 className="text-[24px] font-bold mb-2 sm:mb-4 pr-6">{modalData.title}</h2>
                      <p className="text-gray-700 text-[14px] mb-3 sm:mb-4">{modalData.message}</p>
                    </div>
                  </div>
                )}

                <p className="text-[11px] px-14 py-12 text-gray-600 font-thin px-8 text-center max-w-[1000px] mx-auto">
                  By clicking the 'Submit' button below, I authorize CalCon Mutual Mortgage to contact me about a
                  mortgage by a live agent, artificial or prerecorded voice, and SMS text at my residential or cellular
                  number, dialed manually or by autodialer and to be contacted via email, even if the telephone number
                  is a cellular phone or other service for which the party is charged. I understand that I am not
                  required to provide this consent in order to obtain goods or services from CalCon Mutual Mortgage. I
                  agree to receive disclosures and communications in electronic form and I confirm that I have a working
                  computer system and an internet website browser to view this webpage and this loan inquiry form. I
                  agree that CalCon Mutual Mortgage may contact me by mail, telephone or email in connection with my
                  request even if my telephone number of email address appears on a CalCon Mutual Mortgage internal Do
                  Not Call / Do Not Email List, a State or National Do Not Call Registry, or Do Not E-Mail list, or any
                  other Do Not Contact List.{" "}
                </p>
              </div>
            </div>
            <Footer />
          </>
        );
      }}
    </Query>
  );
};

export default withRouter(DrawRequest);
